import { gzipSync, strToU8 } from 'fflate';

/**
 * Shared compression + payload-encoding primitives for the vTilt SDKs.
 *
 * Both the browser and Node SDKs send batches to the same ingestion endpoint,
 * which decodes `compression=gzip-js` by running `zlib.gunzipSync` over the raw
 * request bytes (see `app/src/lib/tracking/parse-request-body.ts`). Keeping the
 * gzip call, the `compression=` URL flag, and the bigint-safe JSON serializer in
 * one place guarantees the two SDKs stay wire-compatible with each other and the
 * server.
 *
 * `fflate` is a pure-JS zlib implementation, so this module runs unchanged in
 * the browser, Node, Cloudflare Workers and Deno.
 */
/**
 * Compression methods understood by the ingestion endpoint.
 *
 * The user-facing SDK config only exposes `GZipJS` / `None`; the browser
 * request layer additionally picks `Base64` internally for the `sendBeacon`
 * transport (gzip bytes are base64-encoded so the body survives page unload).
 */
var Compression;
(function (Compression) {
    Compression["GZipJS"] = "gzip-js";
    Compression["Base64"] = "base64";
    Compression["None"] = "none";
})(Compression || (Compression = {}));
/**
 * Payloads below this size (bytes) are not worth compressing — the gzip header
 * overhead can make tiny bodies larger, and the CPU cost outweighs the saving.
 */
const MIN_COMPRESSION_SIZE = 1024;
/**
 * JSON serializer that is safe for `bigint` values (which `JSON.stringify`
 * throws on). Used for every request body so the SDKs never crash on a bigint
 * property the caller attached to an event.
 */
function jsonStringify(data) {
    return JSON.stringify(data, (_key, value) => typeof value === "bigint" ? value.toString() : value);
}
/**
 * Append `compression=<value>` to a URL, choosing the right separator.
 *
 * Idempotent w.r.t. the separator only — it does not strip an existing flag.
 * The flag must only be added when the body really is compressed, so the URL
 * never claims a compression the body does not carry (which makes the server's
 * `gunzipSync` throw `Z_BUF_ERROR`).
 */
function withCompressionParam(url, value) {
    const sep = url.includes("?") ? "&" : "?";
    return `${url}${sep}compression=${value}`;
}
/**
 * Gzip a JSON string into bytes suitable for a `compression=gzip-js` POST.
 *
 * `mtime: 0` strips the timestamp from the gzip header so output is
 * deterministic (stable across runs, easier to test and cache).
 */
function gzipToBytes(json) {
    return gzipSync(strToU8(json), { mtime: 0 });
}

/**
 * UUID v7 generator (no runtime dependencies).
 *
 * UUID v7 encodes a 48-bit Unix millisecond timestamp in the most-significant
 * bits, so ids sort chronologically — ideal for an event `$insert_id` that the
 * ingestion side uses for idempotency and ordering. The remaining bits are
 * cryptographically random when a CSPRNG is available, with a `Math.random`
 * fallback for exotic runtimes.
 *
 * Layout (RFC 9562):
 *   - bytes 0..5  : 48-bit big-endian timestamp (ms since epoch)
 *   - byte 6      : version (0b0111) in the high nibble + random low nibble
 *   - byte 8      : variant (0b10) in the high bits + random low bits
 *   - remaining   : random
 */
function getRandomBytes(length) {
    const bytes = new Uint8Array(length);
    const cryptoObj = globalThis.crypto;
    if (cryptoObj && typeof cryptoObj.getRandomValues === "function") {
        cryptoObj.getRandomValues(bytes);
        return bytes;
    }
    // Fallback: non-cryptographic randomness. Acceptable for an id used only
    // for ordering/idempotency, never for security.
    for (let i = 0; i < length; i++) {
        bytes[i] = Math.floor(Math.random() * 256);
    }
    return bytes;
}
const HEX = [];
for (let i = 0; i < 256; i++) {
    HEX.push((i + 0x100).toString(16).slice(1));
}
/** Generate a UUID v7 string. */
function uuidv7() {
    const timestamp = Date.now();
    const bytes = new Uint8Array(16);
    // 48-bit big-endian timestamp. Use Math.floor(/2**n) to avoid the 32-bit
    // truncation that bitwise operators would impose on a ms-precision value.
    bytes[0] = Math.floor(timestamp / 2 ** 40) & 0xff;
    bytes[1] = Math.floor(timestamp / 2 ** 32) & 0xff;
    bytes[2] = Math.floor(timestamp / 2 ** 24) & 0xff;
    bytes[3] = Math.floor(timestamp / 2 ** 16) & 0xff;
    bytes[4] = Math.floor(timestamp / 2 ** 8) & 0xff;
    bytes[5] = timestamp & 0xff;
    bytes.set(getRandomBytes(10), 6);
    // Version 7 in the high nibble of byte 6.
    bytes[6] = (bytes[6] & 0x0f) | 0x70;
    // Variant 0b10 in the high bits of byte 8.
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    return (HEX[bytes[0]] +
        HEX[bytes[1]] +
        HEX[bytes[2]] +
        HEX[bytes[3]] +
        "-" +
        HEX[bytes[4]] +
        HEX[bytes[5]] +
        "-" +
        HEX[bytes[6]] +
        HEX[bytes[7]] +
        "-" +
        HEX[bytes[8]] +
        HEX[bytes[9]] +
        "-" +
        HEX[bytes[10]] +
        HEX[bytes[11]] +
        HEX[bytes[12]] +
        HEX[bytes[13]] +
        HEX[bytes[14]] +
        HEX[bytes[15]]);
}

/**
 * Minimal typed event emitter (no runtime dependencies).
 *
 * Surfaces lifecycle hooks (e.g. `capture`, `flush`, `error`) without pulling
 * in Node's `events` module — which keeps the bundle portable to edge runtimes
 * (Cloudflare Workers, Deno) that may not polyfill it.
 *
 * Listener exceptions are swallowed so a buggy subscriber can never break the
 * pipeline that emits the event.
 *
 * Note: the browser SDK keeps its own richer `SimpleEventEmitter`
 * (string-keyed, with `once`/wildcard/logger integration) because it is wired
 * into many browser-only features. This emitter is the minimal typed primitive
 * shared by server-side / headless SDKs.
 */
class EventEmitter {
    constructor() {
        this._listeners = {};
    }
    /**
     * Subscribe to an event. Returns an unsubscribe function.
     */
    on(name, callback) {
        var _a;
        const set = ((_a = this._listeners)[name] ?? (_a[name] = new Set()));
        set.add(callback);
        return () => {
            set.delete(callback);
        };
    }
    /**
     * Emit an event to all subscribers. Listener errors are isolated.
     */
    emit(name, payload) {
        const set = this._listeners[name];
        if (!set) {
            return;
        }
        for (const callback of set) {
            try {
                callback(payload);
            }
            catch {
                // A listener must never break the pipeline.
            }
        }
    }
    /** Remove every listener (used on shutdown). */
    removeAll() {
        this._listeners = {};
    }
}

export { Compression, EventEmitter, MIN_COMPRESSION_SIZE, gzipToBytes, jsonStringify, uuidv7, withCompressionParam };
//# sourceMappingURL=index.mjs.map
